/*
 * Copyright (c) 2023. American Express Company
 */

// third party
import React, { useLayoutEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { IconInfo } from '@americanexpress/dls-react';

// models and services
import UnitService from 'src/services/unit.service';
import MentorDialogViewModel from 'src/types/view-models/mentor-dialog.view.model';

// components
import MentorButtons from 'src/components/mentors/MentorButtons';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import styles from './Mentor.module.scss';
import Selectors from 'src/utils/selectors';

export default function MentorDialog({
    mode,
    icon,
    headerText,
    body,
    position,
    buttonOneText,
    buttonOneHandler,
    buttonTwoText,
    buttonTwoHandler,
    infoText,
    className,
    isOpen,
    onKeyUp
}: MentorDialogViewModel): JSX.Element {
    const [dialogPosition, setDialogPosition] = useState<number>();

    /** Positioning the dialog in the center of the jaeger container (timeline) */
    const handlePositioning = (): void => {
        const _dialogPosition: number = UnitService.getJaegerContainerCenter();
        setDialogPosition(_dialogPosition);
    };

    /** Handles positioning of the dialog, on mount and whenever the window resizes, if the position prop is 'center' */
    useLayoutEffect(() => {
        if (position === 'center') {
            handlePositioning();
            window.addEventListener('resize', handlePositioning);
            return () => window.removeEventListener('resize', handlePositioning);
        }
    }, [position]);

    return (
        <Dialog
            className={`${styles.mentorContainer} ${className}`}
            aria-labelledby="customized-dialog-title"
            open={isOpen}
            maxWidth="md"
            onKeyUp={(e: any) => {
                void onKeyUp(e, buttonOneHandler);
            }}
        >
            <div
                className={`${styles.mentorDialog} ${
                    mode === 'dialog' ? styles.dialogShownBlock : styles.dialogHidden
                }`}
                style={{ position: 'fixed', top: '50%', left: dialogPosition }}
                data-cy={Selectors.General.MENTOR}
            >
                <div className={styles.mentorDialogHeader}>
                    {icon && <div className={styles.mentorDialogIcon}>{icon}</div>}
                    <Typography variant={TypographyVariants.HEADING_S} white>
                        {headerText}
                    </Typography>
                </div>
                <Typography variant={TypographyVariants.BODY} white>
                    {body}
                </Typography>
                <MentorButtons
                    buttonOneText={buttonOneText}
                    buttonOneHandler={buttonOneHandler}
                    buttonTwoText={buttonTwoText}
                    buttonTwoHandler={buttonTwoHandler}
                />
                {infoText && (
                    <div className={styles.infoSection}>
                        <IconInfo />
                        <Typography variant={TypographyVariants.LEGAL_PRIMARY} white>
                            {infoText}
                        </Typography>
                    </div>
                )}
            </div>
        </Dialog>
    );
}
